import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import DataTable from "../../components/common/DataTable";
import { api_paths } from "../../config/api.paths";
import API from "../../config/axios";
import { formatToUSDate } from "../../utils/dateFormat";
import { toastErrorMessage, toastSuccessMessage } from "../../utils/utils";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { SET_LOADING } from "../../redux/user";
import LeftArrow from "../../assets/images/committee/ArrowLeft.svg";
// 🔹 Status Badge Component
const StatusBadge = ({ status }) => {
  const classes = {
    Active: "badge bg-success-subtle",
    Free: "badge bg-warning-subtle",
    Expired: "badge bg-danger-subtle",
    Pending: "badge bg-danger-subtle",
  };

  return <span className={`${classes[status]} px-3 py-2 rounded-3`}>{status}</span>;
};

export default function ManageMembership() {
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.USER_DATA);
  const [members, setMembers] = useState([]);
  const [plan, setPlan] = useState(null);
  const dispatch = useDispatch();

  // 🔹 Process payment for pending members (integrated from RegisterMembership)
  const handleProcessToPay = async () => {
    try {
      if (!plan?._id) {
        alert("Plan is not available.");
        return;
      }

      // If there are no billable members and primary is not included, nothing to pay
      const primaryIncluded = !user?.is_membership_active;
      if (memberCount === 0 && !primaryIncluded) {
        toastErrorMessage("No pending billable members to process payment for.");
        return;
      }

      dispatch(SET_LOADING(true));

      // Send only family member ids (exclude primary)
      const familyMemberIds = billablePendingMembers
        .filter((m) => m.relationship !== "Primary")
        .map((m) => m._id);

      const payload = {
        family_members: familyMemberIds,
        payment_type: "membership",
        plan_id: plan._id,
        amount: totalAmount,
        primary_included: primaryIncluded,
      };

      console.log("PAYMENT PAYLOAD:", payload);

      const response = await API.post(
        `${api_paths.member.payment.PROCESS_TO_PAY_MEMBERSHIP}?type=adding-members`,
        payload
      );

      if (response.data?.payload?.token) {
        const token = response.data?.payload?.token;

        // Create a POST form dynamically to open the HPP
        const form = document.createElement("form");
        form.method = "POST";
        form.action = `${process.env.REACT_APP_PAYMENT_GATEWAY_URL}hosted-payments`;

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "ssl_txn_auth_token";
        input.value = token;

        form.appendChild(input);
        document.body.appendChild(form);

        dispatch(SET_LOADING(false));

        form.submit();

        toastSuccessMessage("Payment initiated successfully!");
      } else {
        throw new Error("No payment token returned");
      }
    } catch (err) {
      console.error("Payment error:", err);
      dispatch(SET_LOADING(false));
      toastErrorMessage("Failed to initiate payment. Please try again.");
    }
  };

  // 🔹 Prepare Primary User Row
  const primaryRow = useMemo(() => {
    if (!user) return null;

    const cleanDate = user.date_of_birth;
    return {
      _id: "primary-row",
      first_name: user.first_name,
      middle_name: user.middle_name || "",
      last_name: user.last_name,
      date_of_birth: cleanDate ? formatToUSDate(cleanDate) : "",
      gender: user?.gender ? user.gender.toLowerCase() : "",
      marital_status: user.marital_status || "",
      relationship: "Primary",
      is_membership_active: user.is_membership_active,
      is_child: false,
      membership_id: user.membership_id,
      membership_expiry_date: user.membership_expiry_date,
    };
  }, [user]);

  // 🔹 Fetch API Data
  const fetchAll = async () => {
    try {
      dispatch(SET_LOADING(true));
      const [memberRes, planRes] = await Promise.all([
        API.get(api_paths.member.family_member.GET_MEMBER),
        API.get(api_paths.member.membership.GET_PLAN),
      ]);

      setMembers(memberRes.data?.payload?.result || []);

      const plans = planRes.data?.payload?.result || [];
      if (plans.length > 0) setPlan(plans[0]);
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      dispatch(SET_LOADING(false)); // STOP LOADING
    }
  };

  // 🔹 Determine membership status
  const getMembershipStatus = (m) => {
    if (m?.is_free_membership) return "Free";
    if (m?.is_membership_active) return "Active";

    const expiryDate = m?.membership_expiry_date;

    // If expiryDate is null or invalid, return "Pending"
    if (!expiryDate || isNaN(new Date(expiryDate))) return "Pending";

    // Compare valid expiry date with the current date
    const expiry = new Date(expiryDate);
    return expiry < new Date() ? "Expired" : "Pending";
  };

  // 🔹 Combine user + family members
  const allMembers = useMemo(() => {
    if (!primaryRow) return [];
    const visibleFamily = members.filter((m) => !m.is_child);
    return [primaryRow, ...visibleFamily];
  }, [primaryRow, members]);

  // 🔹 Split Active + Pending
  const { activeMembers, pendingMembers } = useMemo(() => {
    const active = [];
    const pending = [];

    allMembers.forEach((m) => {
      const status = getMembershipStatus(m);
      if (status === "Active") active.push(m);
      else pending.push(m);
    });

    return { activeMembers: active, pendingMembers: pending };
  }, [allMembers]);

  // 🔹 Table Columns
  const baseColumns = [
    {
      key: "name",
      label: "Name",
      render: (_, row) =>
        `${row.first_name} ${row.middle_name || ""} ${row.last_name}`
          .replace(/\s+/g, " ")
          .trim(),
    },
    {
      key: "date_of_birth",
      label: "Date of Birth",
      render: (val) => (val ? formatToUSDate(val) : "-"),
    },
    {
      key: "gender",
      label: "Gender",
      render: (val) => <span className="text-capitalize">{val}</span>,
    },
    { key: "relationship", label: "Relation" },
    {
      key: "membership_status",
      label: "Membership",
      render: (_, row) => <StatusBadge status={getMembershipStatus(row)} />,
    },
  ];

  const activeColumns = [
    ...baseColumns,
    {
      key: "membership_expiry_date",
      label: "Expiry Date",
      render: (val) => (val ? formatToUSDate(val) : "-"),
    },
  ];

  const pendingColumns = [
    ...baseColumns,
    {
      key: "membership_expiry_date",
      label: "Expired At",
      render: (val, row) =>
        getMembershipStatus(row) === "Expired" && val ? formatToUSDate(val) : "-",
    },
  ];

  const billablePendingMembers = useMemo(
    () => pendingMembers.filter((m) => getMembershipStatus(m) !== "Free"),
    [pendingMembers]
  );

  // 🔹 Billing Computation
  const memberCount = billablePendingMembers.length;
  const amount = plan?.amount || 0;

  // manage discount_till_date: discount applies ONLY when discount_till_date is provided AND still valid (inclusive).
  const discountAmount = plan?.discount_amount || 0;
  const discountTillDate = plan?.discount_till_date || null;
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const discountValid =
    !!discountAmount &&
    !!discountTillDate &&
    new Date(discountTillDate).setHours(0, 0, 0, 0) >= todayStart.getTime();

  const effectiveDiscount = discountValid ? discountAmount : 0;
  const totalDiscount = memberCount * effectiveDiscount;
  const totalAmount = memberCount * amount - totalDiscount;

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="container py-4">
      {/* ACTIVE MEMBERS */}
      {/* <button
        className="btn btn-orange-md"
        onClick={() => navigate("/transactions")}
      >
        Transactions
      </button> */}

      {activeMembers.length > 0 && (
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex">
              <img
                className="cursor-pointer"
                src={LeftArrow}
                alt="back-arrow"
                onClick={() => {
                  navigate(-1);
                }}
              />
              <h1 className="mb-0 fw-bold text-main f-20 ms-3">Membership</h1>
            </div>
            <button className="btn btn-orange-md" onClick={() => navigate("/transactions")}>
              Transactions
            </button>
          </div>

          <DataTable columns={activeColumns} data={activeMembers} />
        </div>
      )}

      {/* PENDING MEMBERS */}
      {pendingMembers.length > 0 && (
        <div>
          <h1 className="mb-4 pt-2 fw-bold text-main f-20">Pending</h1>
          <DataTable
            columns={pendingColumns}
            data={pendingMembers}
            footer={
              plan && (
                <div
                  className="d-flex justify-content-between align-items-center p-3"
                  style={{
                    backgroundColor: "#FFF7EB",
                    borderTop: "1px solid #efefef",
                  }}
                >
                  {/* LEFT */}
                  <div>
                    <p className="fw-semibold mb-0">Membership Fees</p>
                    <p className="mb-0">{plan.plan_name}</p>
                  </div>

                  {/* MIDDLE */}
                  <div>
                    <p className="fw-semibold mb-0">
                      Total Member(s) {memberCount} × ${amount}
                    </p>
                    {plan?.discount_amount && discountValid && (
                      <p className="mb-0">(${totalDiscount} discount applied)</p>
                    )}
                  </div>

                  {/* RIGHT + BUTTON */}
                  <div className="d-flex align-items-center gap-3 text-end">
                    <div>
                      {plan?.discount_amount && discountValid && (
                        <p className="mb-0">
                          Total Discount:{" "}
                          <span className="fw-semibold ms-1">-${totalDiscount}</span>
                        </p>
                      )}

                      <p className="mb-0">
                        <b>Total Amount: </b>
                        <span className="fw-bold">${totalAmount}</span>
                      </p>
                    </div>

                    <button
                      className="btn btn-orange px-4 ms-3"
                      onClick={() => {
                        handleProcessToPay();
                      }}
                    >
                      Process to Pay
                    </button>
                  </div>
                </div>
              )
            }
          />{" "}
        </div>
      )}

      {/* BILLING BOX */}
    </div>
  );
}
